import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { crmFetch } from './crmApi';
import { JOB_STATUSES, EMPLOYMENT_TYPES, FEE_TYPES, contactName } from './constants';

const EMPTY = {
  accountId: '', contactId: 'none', title: '', status: 'open', employmentType: 'permanent',
  seniority: '', location: '', salaryMin: '', salaryMax: '', currency: 'EUR',
  feeType: 'percentage', feePercentage: '', feeAmount: '', targetDate: '', description: '',
};

export const JobModal = ({ open, onOpenChange, onSaved, job, defaultAccountId, accountName }) => {
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!job;
  const accountLocked = isEdit || !!defaultAccountId;

  useEffect(() => {
    if (!open) return;
    setForm(job ? {
      accountId: job.account_id || '', contactId: job.contact_id || 'none', title: job.title || '',
      status: job.status || 'open', employmentType: job.employment_type || 'permanent',
      seniority: job.seniority || '', location: job.location || '',
      salaryMin: job.salary_min ?? '', salaryMax: job.salary_max ?? '', currency: job.currency || 'EUR',
      feeType: job.fee_type || 'percentage', feePercentage: job.fee_percentage ?? '', feeAmount: job.fee_amount ?? '',
      targetDate: job.target_date || '', description: job.description || '',
    } : { ...EMPTY, accountId: defaultAccountId || '' });
    if (!accountLocked) crmFetch('/accounts').then((r) => setAccounts(r.data || []));
  }, [open, job, defaultAccountId, accountLocked]);

  const activeAccountId = form.accountId;
  useEffect(() => {
    if (!open || !activeAccountId) { setContacts([]); return; }
    crmFetch(`/contacts?accountId=${activeAccountId}`).then((r) => setContacts(r.data || []));
  }, [open, activeAccountId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!accountLocked && !form.accountId) { toast.error('Please select an account'); return; }
    if (!form.title.trim()) { toast.error('Job title is required'); return; }
    setSaving(true);
    try {
      const payload = {
        accountId: form.accountId,
        contactId: form.contactId === 'none' ? null : form.contactId,
        title: form.title, status: form.status, employmentType: form.employmentType,
        seniority: form.seniority, location: form.location,
        salaryMin: form.salaryMin, salaryMax: form.salaryMax, currency: form.currency,
        feeType: form.feeType, feePercentage: form.feePercentage, feeAmount: form.feeAmount,
        targetDate: form.targetDate || null, description: form.description,
      };
      const res = isEdit
        ? await crmFetch(`/jobs/${job.id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : await crmFetch('/jobs', { method: 'POST', body: JSON.stringify(payload) });
      if (!res.success) { toast.error(res.message || 'Failed to save job'); return; }
      toast.success(isEdit ? 'Job updated' : 'Job created');
      onOpenChange(false);
      onSaved && onSaved(res.data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="job-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Job' : 'New Job'}</DialogTitle>
          <DialogDescription>A recruitment opening for a client account.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {accountLocked && (
            <div>
              <Label className="mb-1.5 block">Account</Label>
              <div className="flex items-center gap-2 px-3 py-2 rounded-md border bg-gray-50 text-sm text-gray-700" data-testid="job-account-locked">
                <Building2 className="w-4 h-4 text-gray-400" />
                {accountName || job?.accountName || job?.account?.name || 'Selected account'}
              </div>
            </div>
          )}
          {!accountLocked && (
            <div>
              <Label className="mb-1.5 block">Account *</Label>
              <Select value={form.accountId} onValueChange={(v) => { set('accountId', v); set('contactId', 'none'); }}>
                <SelectTrigger data-testid="job-account-select"><SelectValue placeholder="Select account" /></SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="mb-1.5 block">Job Title *</Label>
            <Input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g. Enterprise Architect" data-testid="job-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">Status</Label>
              <Select value={form.status} onValueChange={(v) => set('status', v)}>
                <SelectTrigger data-testid="job-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {JOB_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">Hiring Contact</Label>
              <Select value={form.contactId} onValueChange={(v) => set('contactId', v)} disabled={!form.accountId}>
                <SelectTrigger data-testid="job-contact-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No contact</SelectItem>
                  {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{contactName(c)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">Employment Type</Label>
              <Select value={form.employmentType} onValueChange={(v) => set('employmentType', v)}>
                <SelectTrigger data-testid="job-employment-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EMPLOYMENT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">Seniority</Label>
              <Input value={form.seniority} onChange={(e) => set('seniority', e.target.value)} placeholder="e.g. Senior" data-testid="job-seniority-input" />
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">Location</Label>
            <Input value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="e.g. Frankfurt, DE" data-testid="job-location-input" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="mb-1.5 block">Salary Min</Label>
              <Input type="number" value={form.salaryMin} onChange={(e) => set('salaryMin', e.target.value)} data-testid="job-salary-min-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Salary Max</Label>
              <Input type="number" value={form.salaryMax} onChange={(e) => set('salaryMax', e.target.value)} data-testid="job-salary-max-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Currency</Label>
              <Input value={form.currency} onChange={(e) => set('currency', e.target.value.toUpperCase())} maxLength={3} data-testid="job-currency-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">Fee Type</Label>
              <Select value={form.feeType} onValueChange={(v) => set('feeType', v)}>
                <SelectTrigger data-testid="job-fee-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FEE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">{form.feeType === 'fixed' ? 'Fee Amount' : 'Fee %'}</Label>
              {form.feeType === 'fixed'
                ? <Input type="number" value={form.feeAmount} onChange={(e) => set('feeAmount', e.target.value)} data-testid="job-fee-amount-input" />
                : <Input type="number" value={form.feePercentage} onChange={(e) => set('feePercentage', e.target.value)} data-testid="job-fee-percentage-input" />}
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">Target Fill Date</Label>
            <Input type="date" value={form.targetDate} onChange={(e) => set('targetDate', e.target.value)} data-testid="job-target-date-input" />
          </div>
          <div>
            <Label className="mb-1.5 block">Description</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} data-testid="job-description-input" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="job-cancel-btn">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="job-save-btn">
              {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Job')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
